
import React, { useState } from 'react';
import { Shield, Target, Award, User, Footprints, Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { teamsAPI } from '../services/api';

// American Football Positions
const POSITIONS = {
    Offense: ['QB', 'HB', 'FB', 'WR', 'TE', 'C', 'OG', 'OT'],
    Defense: ['DT', 'DE', 'MLB', 'OLB', 'CB', 'S'],
    Special: ['K', 'P']
};

const PositionSelectionModal = ({ open, onOpenChange, onComplete, username, teamId }) => {
    const [selectedPositions, setSelectedPositions] = useState([]);
    const [loading, setLoading] = useState(false);

    const togglePosition = (pos) => {
        if (selectedPositions.includes(pos)) {
            setSelectedPositions(selectedPositions.filter(p => p !== pos));
        } else {
            if (selectedPositions.length >= 3) return; // Limit to 3
            setSelectedPositions([...selectedPositions, pos]);
        }
    };

    const handleSave = async () => {
        if (selectedPositions.length === 0) return;
        setLoading(true);
        try {
            await teamsAPI.updateMemberProfile(teamId, { positions: selectedPositions });
            onComplete();
        } catch (error) {
            console.error("Failed to update position:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSkip = () => {
        // Save flag to local storage to not show again
        localStorage.setItem(`position_prompt_skipped_${username}`, 'true');
        onComplete();
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#1a1a1b] border-gray-800 text-white max-w-md">
                <DialogHeader>
                    <DialogTitle className="text-xl font-bold flex items-center gap-2">
                        <User className="w-5 h-5 text-blue-500" />
                        Select Your Position
                    </DialogTitle>
                    <DialogDescription className="text-gray-400">
                        Let your coach and teammates know where you play. You can select up to 3 positions.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    {/* Offense */}
                    <div>
                        <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-3">
                            <Target className="w-4 h-4 text-green-500" /> Offense
                        </h4>
                        <div className="flex flex-wrap gap-2">
                            {POSITIONS.Offense.map(pos => (
                                <button
                                    key={pos}
                                    onClick={() => togglePosition(pos)}
                                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${selectedPositions.includes(pos)
                                        ? 'bg-blue-600 border-blue-600 text-white'
                                        : 'bg-[#0a0a0b] border-gray-700 text-gray-400 hover:border-gray-500'
                                        }`}
                                >
                                    {pos}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Defense */}
                    <div>
                        <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-3">
                            <Shield className="w-4 h-4 text-red-500" /> Defense
                        </h4>
                        <div className="flex flex-wrap gap-2">
                            {POSITIONS.Defense.map(pos => (
                                <button
                                    key={pos}
                                    onClick={() => togglePosition(pos)}
                                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${selectedPositions.includes(pos)
                                        ? 'bg-blue-600 border-blue-600 text-white'
                                        : 'bg-[#0a0a0b] border-gray-700 text-gray-400 hover:border-gray-500'
                                        }`}
                                >
                                    {pos}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Special Teams */}
                    <div>
                        <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-3">
                            <Footprints className="w-4 h-4 text-yellow-500" /> Special Teams
                        </h4>
                        <div className="flex flex-wrap gap-2">
                            {POSITIONS.Special.map(pos => (
                                <button
                                    key={pos}
                                    onClick={() => togglePosition(pos)}
                                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${selectedPositions.includes(pos)
                                        ? 'bg-blue-600 border-blue-600 text-white'
                                        : 'bg-[#0a0a0b] border-gray-700 text-gray-400 hover:border-gray-500'
                                        }`}
                                >
                                    {pos}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-3 mt-2">
                    <Button
                        onClick={handleSave}
                        disabled={selectedPositions.length === 0 || loading}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        {loading ? 'Saving...' : 'Save Positions'}
                    </Button>
                    <button
                        onClick={handleSkip}
                        className="text-gray-500 text-sm hover:text-gray-300 transition-colors"
                    >
                        I don&apos;t have a position yet
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default PositionSelectionModal;
